import { ChevronRightIcon } from '@heroicons/react/20/solid';
import React from 'react';
import {
  type AriaBreadcrumbsProps,
  useBreadcrumbItem,
  useBreadcrumbs,
  AriaBreadcrumbItemProps,
} from 'react-aria';
import { Link } from 'react-router-dom';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

interface BreadcrumbsProps extends AriaBreadcrumbsProps {
  children: React.ReactElement[] | React.ReactElement;
}

export function Breadcrumbs({ children, ...props }: BreadcrumbsProps) {
  const { navProps } = useBreadcrumbs(props);
  const childCount = React.Children.count(children);

  return (
    <nav {...navProps}>
      <ol className="flex items-center space-x-4">
        {React.Children.map(children, (child, i) =>
          React.cloneElement(child, { isCurrent: i === childCount - 1 })
        )}
      </ol>
    </nav>
  );
}

export function BreadcrumbItem({
  to,
  isDisabled,
  isCurrent,
  className,
  style,
  ...props
}: AriaBreadcrumbItemProps & {
  to?: string;
  className?: ((isDisabled?: boolean, isCurrent?: boolean) => string) | string;
  style?: React.CSSProperties | undefined;
}) {
  const ref = React.useRef(null);
  const { itemProps } = useBreadcrumbItem(
    { ...props, elementType: 'span' },
    ref
  );
  return (
    <TooltipPrimitive.Provider>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <li className="flex items-center">
            {to ? (
              <Link
                to={to}
                {...itemProps}
                ref={ref}
                style={style}
                className={
                  className
                    ? typeof className === 'string'
                      ? className
                      : className(isDisabled, isCurrent)
                    : `  text-gray-700  group-[.defaultSidebar]:text-white  text-sm truncate   max-w-[300px]
          ${
            isDisabled
              ? 'cursor-default font-medium hover:text-gray-400 hover:group-[.defaultSidebar]:text-primary-100'
              : 'cursor-pointer font-medium hover:text-gray-400 hover:group-[.defaultSidebar]:text-primary-100'
          }
          ${
            isCurrent
              ? 'font-semibold hover:text-gray-500 hover:group-[.defaultSidebar]:text-primary-100'
              : 'font-medium hover:text-gray-400 hover:group-[.defaultSidebar]:text-primary-100'
          }
        `
                }
              >
                {props.children}
              </Link>
            ) : (
              <span
                {...itemProps}
                ref={ref}
                style={style}
                className={
                  className
                    ? typeof className === 'string'
                      ? className
                      : className(isDisabled, isCurrent)
                    : `text-sm   text-gray-700  group-[.defaultSidebar]:text-white w-full max-w-56 truncate
          ${
            isDisabled || isCurrent
              ? 'cursor-default font-medium hover:text-gray-400 hover:group-[.defaultSidebar]:text-primary-50'
              : 'cursor-pointer font-medium hover:text-gray-400 hover:group-[.defaultSidebar]:text-primary-50'
          }
          ${
            isCurrent
              ? 'font-semibold hover:text-gray-700 '
              : 'font-medium hover:text-gray-400 hover:group-[.defaultSidebar]:text-primary-50'
          }
        `
                }
              >
                {props.children}
              </span>
            )}

            {!isCurrent && (
              <ChevronRightIcon
                className="ml-4 h-5 w-5 flex-shrink-0 text-gray-500 group-[.defaultSidebar]:text-white inline-block"
                aria-hidden="true"
              />
            )}
          </li>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            className="rounded-md px-4 py-2.5 text-sm leading-none text-violet-11 bg-white shadow-lg select-none z-50"
            sideOffset={10}
          >
            {props.children}
            <TooltipPrimitive.Arrow className="fill-white" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export function test() {
  return (
    <Breadcrumbs>
      <BreadcrumbItem
        to="#"
        className={(isDisabled, isCurrent) => `${isDisabled ? 'lol' : 'kek'}`}
      >
        Folder 1
      </BreadcrumbItem>
      <BreadcrumbItem to="#">Folder 2</BreadcrumbItem>
      <BreadcrumbItem isCurrent>Folder 3</BreadcrumbItem>
    </Breadcrumbs>
  );
}
